import { useDebouncedValue } from "@mantine/hooks"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"

export type FetchPageArgs = {
  search: string
}

export type FetchPageResponse<TItem> = {
  items: readonly TItem[]
}

export type UseLazyAutocompleteOptions<TItem, TOption> = {
  fetchPage: (args: FetchPageArgs) => Promise<FetchPageResponse<TItem>>
  toOption: (item: TItem) => TOption
  debounceMs?: number
  minChars?: number
  fetchOnFocus?: boolean
  initialSearchValue?: string
  initialEnabled?: boolean
  onDebouncedSearchValueChange?: (value: string) => void
}

export type UseLazyAutocompleteReturn<TOption> = {
  searchValue: string
  setSearchValue: (value: string) => void
  options: readonly TOption[]
  isLoading: boolean
  isRefreshing: boolean
  isOpen: boolean
  isEnabled: boolean
  error: Error | null
  open: () => void
  close: () => void
  onFocus: () => void
  onDropdownOpen: () => void
}

const DEFAULT_DEBOUNCE_MS = 300
const DEFAULT_MIN_CHARS = 1

export const useLazyAutocomplete = <TItem, TOption = string>({
  fetchPage,
  toOption,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minChars = DEFAULT_MIN_CHARS,
  fetchOnFocus = true,
  initialSearchValue = "",
  initialEnabled = false,
  onDebouncedSearchValueChange,
}: UseLazyAutocompleteOptions<
  TItem,
  TOption
>): UseLazyAutocompleteReturn<TOption> => {
  const [searchValue, setSearchValue] = useState(initialSearchValue)
  const [isEnabled, setIsEnabled] = useState(initialEnabled)
  const [isOpen, setIsOpen] = useState(false)
  const [debouncedSearchValue] = useDebouncedValue(searchValue, debounceMs)
  const hookInstanceId = useId()
  const trimmedSearch = debouncedSearchValue.trim()
  const isSearchLongEnough = trimmedSearch.length >= minChars
  const isQueryEnabled = isEnabled && isOpen && isSearchLongEnough

  const onDebouncedSearchValueChangeRef = useRef(onDebouncedSearchValueChange)
  useEffect(() => {
    onDebouncedSearchValueChangeRef.current = onDebouncedSearchValueChange
  }, [onDebouncedSearchValueChange])

  useEffect(() => {
    onDebouncedSearchValueChangeRef.current?.(debouncedSearchValue)
  }, [debouncedSearchValue])

  const {
    data,
    error: queryError,
    isPending,
    isFetching,
  } = useQuery<
    FetchPageResponse<TItem>,
    Error,
    FetchPageResponse<TItem>,
    readonly [string, string, string]
  >({
    queryKey: ["lazy-autocomplete", hookInstanceId, trimmedSearch],
    queryFn: async () => {
      return fetchPage({
        search: trimmedSearch,
      })
    },
    enabled: isQueryEnabled,
    placeholderData: keepPreviousData,
  })

  const options = useMemo(() => {
    return data?.items.map(toOption) ?? []
  }, [data?.items, toOption])
  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const onFocus = useCallback(() => {
    setIsOpen(true)
    if (fetchOnFocus) {
      setIsEnabled(true)
    }
  }, [fetchOnFocus])

  const onDropdownOpen = useCallback(() => {
    setIsOpen(true)
    setIsEnabled(true)
  }, [])

  const error = isSearchLongEnough ? queryError ?? null : null

  return {
    searchValue,
    setSearchValue,
    options,
    isLoading: isPending && isQueryEnabled, // isPending from React Query is true whenever there's no data, including when the query is disabled
    isRefreshing: isFetching && !isPending,
    isOpen,
    isEnabled,
    error,
    open,
    close,
    onFocus,
    onDropdownOpen,
  }
}
